/**
 * Pure helpers for the AI Coach Knock in-app feed (SCO-371) and the producer
 * banner + bell surface. Kept free of the `@knocklabs/client` runtime so they
 * unit-test with plain objects.
 */
import { z } from 'zod';
/** Knock workflow key for the interactive morning check-in (matches the backend trigger). */
export var MORNING_CHECKIN_WORKFLOW_KEY = 'morning-checkin';
/**
 * Workflows delivered TO producers (recipient = producer). `coach-nudge` is the
 * coach's one-tap send — despite the name, the producer receives it. Everything
 * else (morning-checkin, setup/weekend prompts, recaps) is coach/admin-facing
 * and must never surface on the producer banner or bell.
 */
export var PRODUCER_FACING_WORKFLOW_KEYS = [
    'producer-nudge',
    'coach-nudge'
];
/** Knock names the in-app action_url block this; it is metadata, not body text. */
var ACTION_URL_BLOCK_NAME = 'action_url';
/**
 * Items the coach still needs to see/act on: not yet interacted with and not
 * archived. The feed surfaces un-interacted items only — both a UX choice and
 * the compliance posture (no retained history); once a coach answers (or
 * another channel does, first-response-wins), the item drops out.
 */
export function selectActiveFeedItems(items) {
    return items.filter(function (item) { return item.interacted_at === null && item.archived_at === null; });
}
/** Whether a feed item came from the morning-checkin workflow (renders Yes/No). */
export function isMorningCheckinItem(item) {
    var _a;
    return ((_a = item.source) === null || _a === void 0 ? void 0 : _a.key) === MORNING_CHECKIN_WORKFLOW_KEY;
}
/**
 * The displayable body of a feed item: Knock's server-rendered HTML for its
 * textual (markdown/text) blocks, joined.
 *
 * Reads `rendered` (the Liquid-applied output) — NOT `content`, which is the
 * raw, un-interpolated template; reading `content` is what surfaced literal
 * `{{ data.producer_name }}` in the coach feed (SCO-406). The `action_url`
 * block is also `type: "markdown"` but is metadata (it makes the item
 * clickable), so it is excluded — otherwise its raw `{{ vars.app_url }}/…`
 * leaked into the body. `button_set` blocks are rendered as our own Yes/No UI.
 *
 * The returned HTML must be sanitized at the injection site (DOMPurify in
 * CoachKnockFeed) before rendering — `rendered` can embed values like
 * `producer_name` that a salesperson controls but a coach views. Defensive
 * against a malformed/missing blocks array.
 */
export function feedItemBodyHtml(blocks) {
    if (blocks === void 0) { blocks = []; }
    return blocks
        .filter(function (block) {
        var isTextual = block.type === 'markdown' || block.type === 'text';
        return (isTextual &&
            block.name !== ACTION_URL_BLOCK_NAME &&
            Boolean(block.rendered));
    })
        .map(function (block) { return block.rendered; })
        .join('\n');
}
// --- Producer banner + bell selectors (launch requirement, locked 2026-07-02) ---
/** How long an AI Coach message stays visible in the producer bell. */
export var BELL_ROLLOFF_MS = 8 * 60 * 60 * 1000;
/** Whether a feed item was delivered TO a producer (banner/bell candidate). */
export function isProducerFacingItem(item) {
    var _a;
    return PRODUCER_FACING_WORKFLOW_KEYS.includes((_a = item.source) === null || _a === void 0 ? void 0 : _a.key);
}
var insertedAtMs = function (item) { return Date.parse(item.inserted_at); };
/**
 * The bell's day-view: every producer-facing message from the last 8 hours,
 * acknowledged or not (same-day "what was said and when"), newest first. An
 * item exactly 8 hours old has rolled off. No retained history beyond the
 * window — deliberate product/compliance posture, same as the coach feed.
 */
export function selectBellItems(items, now) {
    var cutoffMs = now.getTime() - BELL_ROLLOFF_MS;
    return items
        .filter(function (item) { return isProducerFacingItem(item) &&
        item.archived_at === null &&
        insertedAtMs(item) > cutoffMs &&
        // A misconfigured workflow can deliver an item with no textual
        // body (e.g. only an action_url block) — without this guard it
        // banners as avatars + "Tap to dismiss" around an empty message.
        feedItemBodyHtml(item.blocks).trim() !== ''; })
        .sort(function (a, b) { return insertedAtMs(b) - insertedAtMs(a); });
}
/**
 * The item the persistent top banner shows: the NEWEST producer-facing item
 * inside the 8h window that has not been acknowledged from any surface
 * (first-acknowledgment-wins — `interacted_at` is server-side Knock state, so
 * a tap on the phone clears the desktop banner via the realtime store).
 * Null → no banner.
 *
 * The window matters: without it, yesterday's un-acknowledged pace nudge
 * ("12 to go!") would banner as fresh coaching the next morning — stale,
 * wrong, and against the locked "gone by tomorrow" intent. An un-heard nudge
 * simply expires with the window; the recap covers the day.
 */
export function selectBannerItem(items, now) {
    var _a;
    // Derived from the bell's day-view (already producer-filtered, windowed,
    // and sorted newest-first) so the two surfaces share ONE eligibility rule
    // and can never diverge; the banner is simply the newest un-heard item.
    return ((_a = selectBellItems(items, now).find(function (item) { return item.interacted_at === null; })) !== null && _a !== void 0 ? _a : null);
}
/**
 * Trigger `data` is external input (set by the backend, transported by Knock),
 * so it's parsed rather than trusted. Anything malformed degrades to nulls —
 * the banner falls back to initials, never throws.
 */
var FeedItemAvatarDataSchema = z.object({
    producer_avatar_url: z.string().nullable().optional(),
    coach_avatar_url: z.string().nullable().optional()
});
export function feedItemAvatars(item) {
    var _a, _b;
    var parsed = FeedItemAvatarDataSchema.safeParse(item.data);
    if (!parsed.success) {
        return { producerAvatarUrl: null, coachAvatarUrl: null };
    }
    return {
        producerAvatarUrl: (_a = parsed.data.producer_avatar_url) !== null && _a !== void 0 ? _a : null,
        coachAvatarUrl: (_b = parsed.data.coach_avatar_url) !== null && _b !== void 0 ? _b : null
    };
}
